import { upperFirst } from 'lodash-es';
import { Ref, unref } from 'vue';
import type { Fn } from '@mts-fe/types';
import { isWindow } from '.';

export interface ViewportOffsetResult {
  /**
   * 元素左边距离 body 左边的距离（和 getBoundingClientRect 的 left 一样）
   */
  left: number;
  /**
   * 元素顶边距离 body 顶边的距离（和 getBoundingClientRect 的 top 一样）
   */
  top: number;
  /**
   * 元素右边距离 body 右边的距离
   */
  right: number;
  /**
   * 元素底边距离 body 底边的距离
   */
  bottom: number;
  /**
   * 内容宽度 + 计算后的 right
   */
  rightIncludeBody: number;
  /**
   * 内容高度 + 计算后的 bottom
   */
  bottomIncludeBody: number;
}

const makeDOMRect = (width: number, height: number) =>
  ({ top: 0, left: 0, right: width, bottom: height, width, height }) as DOMRect;

export const useDOMRect = (elementOrRef: Element | Window | Ref<Element | Window | undefined>) => {
  const element = unref(elementOrRef);

  if (isWindow(element)) {
    const width = element.innerWidth;
    const height = element.innerHeight;
    return makeDOMRect(width, height);
  }

  if (element?.getBoundingClientRect) {
    return element.getBoundingClientRect();
  }

  return makeDOMRect(0, 0);
};

const pageXOffset = window.pageXOffset;
const pageYOffset = window.pageYOffset;
const doc = document.documentElement;
/**
 * Get the left and top offset of the current element
 * left: the distance between the leftmost element and the left side of the document
 * top: the distance from the top of the element to the top of the document
 * right: the distance from the far right of the element to the right of the document
 * bottom: the distance from the bottom of the element to the bottom of the document
 * rightIncludeBody: the distance between the leftmost element and the right side of the document
 * bottomIncludeBody: the distance from the bottom of the element to the bottom of the document
 *
 * @description:
 */
export function getViewportOffset(element: Element): ViewportOffsetResult {
  const clientWidth = doc.clientWidth;
  const clientHeight = doc.clientHeight;

  const box = useDOMRect(element);

  const { left: retLeft, top: rectTop, width: rectWidth, height: rectHeight } = box as DOMRect;

  const scrollLeft = (pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
  const scrollTop = (pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
  const offsetLeft = retLeft + pageXOffset;
  const offsetTop = rectTop + pageYOffset;

  const left = offsetLeft - scrollLeft;
  const top = offsetTop - scrollTop;

  return {
    left: left,
    top: top,
    right: clientWidth - rectWidth - left,
    bottom: clientHeight - rectHeight - top,
    rightIncludeBody: clientWidth - left,
    bottomIncludeBody: clientHeight - top,
  };
}

export function hackCss(attr: string, value: string) {
  const prefix: string[] = ['webkit', 'Moz', 'ms', 'OT'];

  const styleObj: any = {};
  prefix.forEach((item) => {
    styleObj[`${item}${upperFirst(attr)}`] = value;
  });
  return {
    ...styleObj,
    [attr]: value,
  };
}

/* istanbul ignore next */
export function on(
  element: Element | HTMLElement | Document | Window,
  event: string,
  handler: EventListenerOrEventListenerObject,
): void {
  if (element && event && handler) {
    element.addEventListener(event, handler, false);
  }
}

/* istanbul ignore next */
export function off(
  element: Element | HTMLElement | Document | Window,
  event: string,
  handler: Fn,
): void {
  if (element && event && handler) {
    element.removeEventListener(event, handler, false);
  }
}

/* istanbul ignore next */
export function once(el: HTMLElement, event: string, fn: EventListener): void {
  const listener = function (this: any, ...args: unknown[]) {
    if (fn) {
      fn.apply(this, args as [evt: Event]);
    }
    off(el, event, listener);
  };
  on(el, event, listener);
}

export function triggerWindowResize() {
  const event = document.createEvent('HTMLEvents');
  event.initEvent('resize', true, true);
  (event as any).eventType = 'message';
  window.dispatchEvent(event);
}

/**
 * 向上遍历 DOM 树查找指定属性值
 * 从当前元素开始，逐级向上查找父元素，直到找到指定属性或到达根元素
 *
 * @param element - 起始元素
 * @param attributeName - 要查找的属性名
 * @param options - 配置选项
 * @returns 属性值 或 null
 *
 * @example
 * ```typescript
 * const field = findAttributeInParents(element, 'data-field', {
 *   stopAtClasses: ['ant-form-item']
 * });
 * ```
 */
export function findAttributeInParents(
  element: HTMLElement,
  attributeName: string,
  options?: {
    /** 最大遍历深度，0 表示无限制，默认 0 */
    maxDepth?: number;
    /** 遇到这些类名时停止查找 */
    stopAtClasses?: string[];
  },
): string | null {
  const maxDepth = options?.maxDepth || 0;
  const stopAtClasses = options?.stopAtClasses || [];
  let depth = 0;
  let current: HTMLElement | null = element.parentElement;
  while (current) {
    if (maxDepth > 0 && depth >= maxDepth) break;

    if (stopAtClasses.length > 0 && current.classList) {
      const hasStopClass = stopAtClasses.some((i) => current!.classList.contains(i));
      if (hasStopClass) break;
    }

    const value = current.getAttribute(attributeName);
    if (value !== null) return value;

    depth++;
    current = current.parentElement;
  }
  return null;
}

// 缓存映射，用于存储预编译的正则表达式
const regexCacheMap = new Map<string, RegExp[]>();
/**
 * 通用元素可见性检查方法
 * 检查元素及其祖先是否包含指定的隐藏类名
 *
 * @param element - 要检查的元素
 * @param options - 配置选项
 * @returns boolean - 是否隐藏
 *
 * @example
 * ```typescript
 * isElementHidden(element, { classNames: ['ant-form-item-hidden'] });
 */
export function isElementHidden(
  element: HTMLElement,
  options: {
    /** 要检查的类名数组 */
    classNames: string[];
    /** 向上遍历的最大深度，0 表示无限制，默认 0 */
    maxDepth?: number;
    /** 是否缓存正则表达式，默认 true */
    cacheRegex?: boolean;
    /** 遇到这些类名时停止查找 */
    stopAtClasses?: string[];
  },
): boolean {
  const { classNames, maxDepth = 0, cacheRegex = true, stopAtClasses = [] } = options;

  // 预编译正则表达式并缓存
  let regexCache: RegExp[];
  if (cacheRegex) {
    const cacheKey = classNames.sort().join('|');
    if (regexCacheMap.has(cacheKey)) {
      regexCache = regexCacheMap.get(cacheKey)!;
    } else {
      regexCache = classNames.map((name) => new RegExp(name));
      regexCacheMap.set(cacheKey, regexCache);
    }
  } else {
    regexCache = classNames.map((name) => new RegExp(name));
  }

  let depth = 0;
  let parent = element.parentElement;
  while (parent) {
    if (maxDepth > 0 && depth >= maxDepth) break;

    if (stopAtClasses.length > 0 && parent.classList) {
      const hasStopClass = stopAtClasses.some((className) => parent!.classList.contains(className));
      if (hasStopClass) break;
    }

    const className = parent.className;
    if (typeof className === 'string' && className.length > 0) {
      for (const regex of regexCache) {
        if (regex.test(className)) return true;
      }
    }

    depth++;
    parent = parent.parentElement;
  }
  return false;
}

export function isHidden(elementRef: HTMLElement | Ref<HTMLElement | undefined>) {
  const el = unref(elementRef);
  if (!el) {
    return false;
  }

  const style = window.getComputedStyle(el);
  const hidden = style.display === 'none';

  // offsetParent returns null in the following situations:
  // 1. The element or its parent element has the display property set to none.
  // 2. The element has the position property set to fixed
  const parentHidden = el.offsetParent === null && style.position !== 'fixed';

  return hidden || parentHidden;
}

/** 检测元素从隐藏到可见 */
export function onElementVisible(
  element: HTMLElement,
  callback: () => void,
  options: IntersectionObserverInit = {},
) {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.unobserve(element);
          requestAnimationFrame(() => callback());
        }
      }
    },
    { ...options, threshold: options.threshold || 0.1 },
  );

  observer.observe(element);
  return observer;
}
